import type { User } from '@app-types';

export interface ContractDivisionPreview {
  /** The Division the contract will show, or null for none */
  division: string | null;
  /** An owner's Division is not known to the form, so the API's result may differ from `division` */
  recalculatedOnSave: boolean;
}

/** The Divisions of the owners that have one: how many distinct, and the first one's name. */
function summarizeOwnerDivisions(users: User[], ownerIds: number[]): { count: number; name: string | null } {
  const owners = users.filter((user) => ownerIds.includes(user.id) && user.division_id != null);

  return {
    count: new Set(owners.map((user) => user.division_id)).size,
    name: owners[0]?.division_name || null
  };
}

/** True when the Contract Owners that have a Division are spread over more than one. */
export function ownersSpanDivisions(users: User[], ownerIds: number[]): boolean {
  return summarizeOwnerDivisions(users, ownerIds).count > 1;
}

/**
 * The Division a contract shows in its form, mirroring the rule the API applies when the Owners
 * are saved (ADO-1192): the one Division the owners share, none when no owner has one, and the
 * current Division kept (with a Division Conflict) when they disagree. A new contract has no
 * current Division, so there the last two give none. Owners without a Division carry no signal.
 *
 * Until the Owners change, the current Division stays. When a selected owner is missing from
 * `users` (e.g. inactive) the form cannot tell their Division, so it keeps the current one and
 * flags that the API recalculates it on save instead of claiming a Division it may not get.
 */
export function previewContractDivision(
  users: User[],
  ownerIds: number[],
  currentDivision: string | null,
  ownersChanged: boolean
): ContractDivisionPreview {
  if (!ownersChanged) return { division: currentDivision, recalculatedOnSave: false };

  const hasUnknownOwner = ownerIds.some((id) => !users.some((user) => user.id === id));
  if (hasUnknownOwner) return { division: currentDivision, recalculatedOnSave: true };

  const { count, name } = summarizeOwnerDivisions(users, ownerIds);
  if (count === 1) return { division: name, recalculatedOnSave: false };
  if (count === 0) return { division: null, recalculatedOnSave: false };

  return { division: currentDivision, recalculatedOnSave: false };
}
